// Client mirror of the role-assignment rules in server/routes/auth.js, so the
// People "Role" field never offers a role the server will refuse (Gitea #107).
// Keep each rule in step with the server line cited beside it.
//
// - resolveRoleAssignment (auth.js:1421-1433): a built-in slug is always a
//   role; a custom slug must be a roles row with is_preset = false AND
//   is_system = false (:1430), else 400.
// - Escalation guard (auth.js:1435-1439; POST :1466-1468, PUT :1607-1610):
//   admin / payroll_admin may be granted only by a built-in admin or a program
//   admin. callerIsBuiltinAdmin reads req.user.role, never a permission flag,
//   so a Manager, Payroll Admin or custom role with Manage users is refused.
// - program_admin (POST :1449-1451, PUT :1569-1573): only a program admin.
//   GET /roles never returns it and People never offered it; unchanged here.
// - Own record (PUT :1611-1617): nobody changes their own role - 403 for a
//   non-admin, 400 for an admin. The field is locked on the caller's own login.
// - Re-saving the role a login already has is a no-op (PUT :1588-1595), so the
//   target's current role is always offered, even one the caller couldn't grant:
//   People sends the current role on every Save.

export const BUILTIN_ROLES = Object.freeze(['user', 'manager', 'payroll_admin', 'admin', 'program_admin']);
const PRIVILEGED_BASES = Object.freeze(['admin', 'payroll_admin']);

// What the Role field listed when GET /roles returned nothing (program admins:
// the query is scoped to the caller's tenant, which they don't have).
export const BUILTIN_ROLE_OPTIONS = Object.freeze([
  { slug: 'user', name: 'Employee' },
  { slug: 'manager', name: 'Manager' },
  { slug: 'payroll_admin', name: 'Payroll Admin' },
  { slug: 'admin', name: 'Admin' },
]);

export const isBuiltinAdmin = (user) => ['admin', 'program_admin'].includes(user?.role);

// May `user` give `slug` to someone else?
export const canGrantRole = (user, slug) => {
  if (slug === 'program_admin') return user?.role === 'program_admin';
  if (PRIVILEGED_BASES.includes(slug)) return isBuiltinAdmin(user);
  return true;
};

// The caller's own login (users.id): the Role field is read-only there.
export const isOwnLogin = (user, targetUserId) =>
  user?.id != null && targetUserId != null && String(targetUserId) === String(user.id);

// Options for the Role field as [{ slug, name }]. `roles` = GET /roles rows
// (or [] / undefined -> BUILTIN_ROLE_OPTIONS). `currentSlug` = the slug the
// field holds now (formData.user_role); it stays in the list whatever the
// caller may grant, so an unchanged Save is the server no-op and MUI can
// render the value. Row order (server: is_preset DESC, name) is kept.
export function grantableRoleOptions(user, roles, currentSlug) {
  const source = Array.isArray(roles) && roles.length > 0
    ? roles.filter((r) => BUILTIN_ROLES.includes(r.slug) || (r.is_preset === false && r.is_system === false))
    : BUILTIN_ROLE_OPTIONS;
  const options = source
    .filter((r) => r.slug === currentSlug || canGrantRole(user, r.slug))
    .map((r) => ({ slug: r.slug, name: r.name }));
  if (currentSlug && !options.some((o) => o.slug === currentSlug)) {
    // The current role is not among the rows (rows not loaded, or a custom
    // role under the fallback): still render it so the value shows.
    const builtin = BUILTIN_ROLE_OPTIONS.find((o) => o.slug === currentSlug);
    options.push(builtin || { slug: currentSlug, name: currentSlug });
  }
  return options;
}
